import Navbar from "../../Components/Navigation/Navbar";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
    createWorkHour,
    getEventsPage,
    getMemberMe,
    getMyWorkHours,
    getUpcomingBirthdays,
    patchMemberMe,
    searchMemberDirectory,
} from "../../api/requests";
import AssetBookingMember from "../../Components/Assets/AssetBookingMember";
import "./membersPage.css";

const MembersPage = () => {
    const [events, setEvents] = useState([]);
    const [birthdays, setBirthdays] = useState([]);

    const [me, setMe] = useState(null);
    const [meLoading, setMeLoading] = useState(true);
    const [meError, setMeError] = useState(null);
    const [meSaving, setMeSaving] = useState(false);

    const [dirQuery, setDirQuery] = useState("");
    const [dirLoading, setDirLoading] = useState(false);
    const [dirResults, setDirResults] = useState([]);
    const [dirError, setDirError] = useState(null);

    // Work hours (own)
    const [workHours, setWorkHours] = useState([]);
    const [workHoursLoading, setWorkHoursLoading] = useState(false);
    const [workHoursError, setWorkHoursError] = useState(null);
    const [workHoursSubmitting, setWorkHoursSubmitting] = useState(false);

    const [workForm, setWorkForm] = useState({
        taetigkeit: "",
        datum: "",
        stunden: "",
        details: "",
    });

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            // Don't fail the whole page if one request errors.
            try {
                const ev = await getEventsPage({ upcoming: true });
                if (isMounted) {
                    setEvents(ev.results);
                }
            } catch (e) {
                console.error(e);
            }

            try {
                const bd = await getUpcomingBirthdays(30);
                if (isMounted) {
                    setBirthdays(Array.isArray(bd) ? bd : []);
                }
            } catch (e) {
                console.error(e);
            }

            try {
                if (isMounted) {
                    setWorkHoursLoading(true);
                }
                const wh = await getMyWorkHours();
                if (isMounted) {
                    setWorkHours(Array.isArray(wh) ? wh : []);
                }
            } catch (e) {
                console.error(e);
                if (isMounted) {
                    setWorkHoursError(e);
                    setWorkHours([]);
                }
            } finally {
                if (isMounted) {
                    setWorkHoursLoading(false);
                }
            }

            try {
                const my = await getMemberMe();
                if (isMounted) {
                    setMe(my);
                }
            } catch (e) {
                console.error(e);
                if (isMounted) {
                    setMeError(e);
                }
            } finally {
                if (isMounted) {
                    setMeLoading(false);
                }
            }
        };

        load();

        return () => {
            isMounted = false;
        };
    }, []);

    const upcomingEvents = useMemo(() => {
        const now = new Date();
        return (Array.isArray(events) ? events : [])
            .filter((e) => {
                if (!e?.start_datetime) return false;
                const d = new Date(e.start_datetime);
                return !Number.isNaN(d.getTime()) && d >= now;
            })
            .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
            .slice(0, 5);
    }, [events]);

    const formatEventDate = (dateTimeStr) => {
        if (!dateTimeStr) return "";
        const d = new Date(dateTimeStr);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleDirectorySearch = async (e) => {
        e.preventDefault();
        setDirError(null);
        setDirLoading(true);
        try {
            const res = await searchMemberDirectory(dirQuery.trim());
            setDirResults(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error(err);
            setDirError(err);
            setDirResults([]);
        } finally {
            setDirLoading(false);
        }
    };

    const updateMeFlag = (key, value) => {
        setMe((prev) => ({
            ...(prev || {}),
            [key]: value,
        }));
    };

    const handleSavePrivacy = async () => {
        if (!me) return;
        setMeSaving(true);
        setMeError(null);
        try {
            const payload = {
                directory_visible: !!me.directory_visible,
                show_name: !!me.show_name,
                show_email: !!me.show_email,
                show_phone_private: !!me.show_phone_private,
                show_phone_mobile: !!me.show_phone_mobile,
                show_phone_office: !!me.show_phone_office,
                show_birthday: !!me.show_birthday,
            };
            const updated = await patchMemberMe(payload);
            setMe(updated);
            // refresh dependent widgets
            const bd = await getUpcomingBirthdays(30);
            setBirthdays(Array.isArray(bd) ? bd : []);
        } catch (err) {
            console.error(err);
            setMeError(err);
        } finally {
            setMeSaving(false);
        }
    };

    const refreshWorkHours = async () => {
        setWorkHoursError(null);
        setWorkHoursLoading(true);
        try {
            const wh = await getMyWorkHours();
            setWorkHours(Array.isArray(wh) ? wh : []);
        } catch (e) {
            console.error(e);
            setWorkHoursError(e);
            setWorkHours([]);
        } finally {
            setWorkHoursLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return String(dateStr);
        return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return "";
        const d = new Date(dateTimeStr);
        if (Number.isNaN(d.getTime())) return String(dateTimeStr);
        return d.toLocaleString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleSubmitWorkHour = async (e) => {
        e.preventDefault();
        setWorkHoursError(null);
        setWorkHoursSubmitting(true);
        try {
            const payload = {
                taetigkeit: workForm.taetigkeit.trim(),
                datum: workForm.datum,
                stunden: Number(workForm.stunden),
                details: workForm.details,
            };
            await createWorkHour(payload);
            setWorkForm({ taetigkeit: "", datum: "", stunden: "", details: "" });
            await refreshWorkHours();
        } catch (err) {
            console.error(err);
            setWorkHoursError(err);
        } finally {
            setWorkHoursSubmitting(false);
        }
    };

    return (
        <div className="App">
            <Navbar />
            <Container style={{ marginTop: 16 }}>
                <Row xs={1} lg={2} className="g-3 members-dashboard-grid">
                    <Col>
                        <Card>
                            <Card.Header>Nächste Events</Card.Header>
                            <Card.Body>
                                {upcomingEvents.length === 0 ? (
                                    <div>Keine zukünftigen Events gefunden.</div>
                                ) : (
                                    <ListGroup variant="flush">
                                        {upcomingEvents.map((ev) => (
                                            <ListGroup.Item key={ev.id}>
                                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>
                                                            <Link to={`/event/${ev.id}`}>{ev.event_name}</Link>
                                                        </div>
                                                        <div style={{ opacity: 0.8 }}>{formatEventDate(ev.start_datetime)}</div>
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col>
                        <Card>
                            <Card.Header>Geburtstage der nächsten 30 Tage</Card.Header>
                            <Card.Body>
                                {birthdays.length === 0 ? (
                                    <div>Keine Geburtstage im Zeitraum (oder keine Freigaben).</div>
                                ) : (
                                    <ListGroup variant="flush">
                                        {birthdays.map((b, idx) => (
                                            <ListGroup.Item key={`${b.display_name || "member"}-${b.date}-${idx}`}>
                                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                                    <div style={{ fontWeight: 600 }}>{b.display_name || "(ohne Name)"}</div>
                                                    <div>{b.date}</div>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col>
                        <Card>
                            <Card.Header>Mitglieder-Verzeichnis</Card.Header>
                            <Card.Body>
                                <Form onSubmit={handleDirectorySearch}>
                                    <Row className="g-2">
                                        <Col xs={12} md={8}>
                                            <Form.Control
                                                value={dirQuery}
                                                onChange={(e) => setDirQuery(e.target.value)}
                                                placeholder="Name suchen (Vorname/Nachname)"
                                            />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Button type="submit" disabled={dirLoading} style={{ width: "100%" }}>
                                                {dirLoading ? "Suche..." : "Suchen"}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>

                                {dirError && (
                                    <Alert variant="danger" style={{ marginTop: 12 }}>
                                        Suche fehlgeschlagen.
                                    </Alert>
                                )}

                                <div style={{ marginTop: 12 }}>
                                    {dirLoading ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : dirResults.length === 0 ? (
                                        <div style={{ opacity: 0.8 }}>Keine Treffer (oder keine Freigaben).</div>
                                    ) : (
                                        <ListGroup>
                                            {dirResults.map((m) => (
                                                <ListGroup.Item key={m.id}>
                                                    <div style={{ fontWeight: 600 }}>{m.display_name || "(ohne Name)"}</div>
                                                    <div style={{ marginTop: 6, display: "grid", gap: 2 }}>
                                                        {m.email && <div>E-Mail: {m.email}</div>}
                                                        {m.telefon && <div>Telefon: {m.telefon}</div>}
                                                        {m.telefon_mobil && <div>Mobil: {m.telefon_mobil}</div>}
                                                        {m.telefon_buero && <div>Büro: {m.telefon_buero}</div>}
                                                        {m.birthday && <div>Geburtstag: {m.birthday}</div>}
                                                    </div>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col>
                        <Card>
                            <Card.Header>Arbeitsstunden eintragen</Card.Header>
                            <Card.Body>
                                <Form onSubmit={handleSubmitWorkHour}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Tätigkeit</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={workForm.taetigkeit}
                                            onChange={(e) => setWorkForm((p) => ({ ...p, taetigkeit: e.target.value }))}
                                            required
                                        />
                                    </Form.Group>
                                    <Row className="g-2">
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Datum</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={workForm.datum}
                                                    onChange={(e) => setWorkForm((p) => ({ ...p, datum: e.target.value }))}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Stunden</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="0.25"
                                                    value={workForm.stunden}
                                                    onChange={(e) => setWorkForm((p) => ({ ...p, stunden: e.target.value }))}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Details (optional)</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={workForm.details}
                                            onChange={(e) => setWorkForm((p) => ({ ...p, details: e.target.value }))}
                                        />
                                    </Form.Group>

                                    {workHoursError && (
                                        <Alert variant="danger" className="mt-2">
                                            Speichern fehlgeschlagen.
                                        </Alert>
                                    )}

                                    <Button type="submit" disabled={workHoursSubmitting}>
                                        {workHoursSubmitting ? "Speichere..." : "Eintragen"}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col>
                        <Card>
                            <Card.Header>Meine Arbeitsstunden</Card.Header>
                            <Card.Body>
                                {workHoursLoading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : workHours.length === 0 ? (
                                    <div style={{ opacity: 0.8 }}>Noch keine Einträge.</div>
                                ) : (
                                    <ListGroup>
                                        {workHours.map((w) => (
                                            <ListGroup.Item key={w.id}>
                                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{w.taetigkeit}</div>
                                                        <div style={{ opacity: 0.85 }}>
                                                            {formatDate(w.datum)} · {w.stunden}h
                                                        </div>
                                                        {w.details ? (
                                                            <div style={{ marginTop: 6, opacity: 0.9 }}>{w.details}</div>
                                                        ) : null}
                                                        {w.is_approved ? (
                                                            <div style={{ marginTop: 6, opacity: 0.8 }}>
                                                                Freigegeben von {w.approved_by_username || "?"}
                                                                {w.approved_at ? ` am ${formatDateTime(w.approved_at)}` : ""}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <div>
                                                        {w.is_approved ? (
                                                            <Badge bg="success">Freigegeben</Badge>
                                                        ) : (
                                                            <Badge bg="warning" text="dark">
                                                                Ausstehend
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col>
                        <Card>
                            <Card.Header>Privatsphäre-Einstellungen</Card.Header>
                            <Card.Body>
                                {meLoading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : meError ? (
                                    <Alert variant="danger">
                                        Profil konnte nicht geladen werden.
                                        <div style={{ opacity: 0.8, marginTop: 6 }}>
                                            Falls du gerade erst angelegt wurdest, fehlt ggf. noch ein MemberProfile.
                                        </div>
                                    </Alert>
                                ) : !me ? (
                                    <Alert variant="warning">Kein Profil gefunden.</Alert>
                                ) : (
                                    <>
                                        <Form>
                                            <Form.Check
                                                type="switch"
                                                id="privacy-directory_visible"
                                                label="Im Mitglieder-Verzeichnis sichtbar"
                                                checked={!!me.directory_visible}
                                                onChange={(e) => updateMeFlag("directory_visible", e.target.checked)}
                                            />
                                            <Form.Check
                                                type="switch"
                                                id="privacy-show_name"
                                                label="Name anzeigen"
                                                checked={!!me.show_name}
                                                onChange={(e) => updateMeFlag("show_name", e.target.checked)}
                                            />
                                            <Form.Check
                                                type="switch"
                                                id="privacy-show_email"
                                                label="E-Mail anzeigen"
                                                checked={!!me.show_email}
                                                onChange={(e) => updateMeFlag("show_email", e.target.checked)}
                                            />
                                            <Form.Check
                                                type="switch"
                                                id="privacy-show_phone_private"
                                                label="Telefon (privat) anzeigen"
                                                checked={!!me.show_phone_private}
                                                onChange={(e) => updateMeFlag("show_phone_private", e.target.checked)}
                                            />
                                            <Form.Check
                                                type="switch"
                                                id="privacy-show_phone_mobile"
                                                label="Mobil anzeigen"
                                                checked={!!me.show_phone_mobile}
                                                onChange={(e) => updateMeFlag("show_phone_mobile", e.target.checked)}
                                            />
                                            <Form.Check
                                                type="switch"
                                                id="privacy-show_phone_office"
                                                label="Telefon (Büro) anzeigen"
                                                checked={!!me.show_phone_office}
                                                onChange={(e) => updateMeFlag("show_phone_office", e.target.checked)}
                                            />
                                            <Form.Check
                                                type="switch"
                                                id="privacy-show_birthday"
                                                label="Geburtstag (Tag/Monat) anzeigen"
                                                checked={!!me.show_birthday}
                                                onChange={(e) => updateMeFlag("show_birthday", e.target.checked)}
                                            />
                                        </Form>

                                        <div style={{ marginTop: 12 }}>
                                            <Button onClick={handleSavePrivacy} disabled={meSaving}>
                                                {meSaving ? "Speichere..." : "Speichern"}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} className="members-dashboard-full-row">
                        <AssetBookingMember />
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default MembersPage;
